import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  ShoppingBag,
  Wrench,
  Users,
  ChevronDown,
  Check,
  Building2,
  Shield
} from 'lucide-react';

/**
 * Portal configuration - Scalable for future portals
 * To add a new portal, simply add an entry here with:
 * - id: unique identifier
 * - label: display name
 * - icon: lucide icon component
 * - path: route path
 * - roles: array of roles that can access this portal
 * - color: accent color class
 * - description: brief description
 */
const PORTALS = [
  {
    id: 'admin',
    label: 'Administración',
    icon: Shield,
    path: '/admin',
    roles: ['admin'],
    color: 'text-purple-400 bg-purple-500/10 border-purple-500/30',
    activeColor: 'bg-purple-500/20 border-purple-500/50',
    description: 'Panel de control general'
  },
  {
    id: 'comercial',
    label: 'Portal Comercial',
    icon: ShoppingBag,
    path: '/comercial',
    roles: ['admin', 'comercial'],
    color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
    activeColor: 'bg-emerald-500/20 border-emerald-500/50',
    description: 'Gestión comercial y pipeline'
  },
  {
    id: 'tecnico',
    label: 'Portal Técnico',
    icon: Wrench,
    path: '/tecnico',
    roles: ['admin', 'tecnico'],
    color: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    activeColor: 'bg-amber-500/20 border-amber-500/50',
    description: 'Instalaciones y mantenimiento'
  },
  {
    id: 'cliente',
    label: 'Portal Cliente',
    icon: Users,
    path: '/dashboard',
    roles: ['admin', 'comercial', 'tecnico', 'cliente', 'user'],
    color: 'text-sky-400 bg-sky-500/10 border-sky-500/30',
    activeColor: 'bg-sky-500/20 border-sky-500/50',
    description: 'Vista del cliente'
  },
  // Future portals can be added here:
  // {
  //   id: 'financiero',
  //   label: 'Portal Financiero',
  //   icon: Wallet,
  //   path: '/financiero',
  //   roles: ['admin', 'financiero'],
  //   color: 'text-green-400 bg-green-500/10 border-green-500/30',
  //   activeColor: 'bg-green-500/20 border-green-500/50',
  //   description: 'Facturación y contabilidad'
  // },
];

/**
 * Get accessible portals for a given user role
 */
export function getAccessiblePortals(role) {
  return PORTALS.filter(portal => portal.roles.includes(role));
}

/**
 * Get current portal based on pathname
 */
export function getCurrentPortal(pathname) {
  // Check exact matches first, then prefix matches
  const exactMatch = PORTALS.find(p => p.path === pathname);
  if (exactMatch) return exactMatch;
  
  // Check if path starts with portal path
  return PORTALS.find(p => {
    if (p.path === '/dashboard') {
      return pathname === '/dashboard' || pathname.startsWith('/dashboard/');
    }
    return pathname.startsWith(p.path + '/') || pathname === p.path;
  }) || null;
}

/**
 * PortalSelector Component
 * 
 * A scalable dropdown selector for switching between portals.
 * Automatically filters available portals based on user role.
 * 
 * Props:
 * - variant: 'dropdown' | 'compact' | 'full' - display style
 * - showDescription: boolean - show portal description
 * - className: string - additional CSS classes
 */
export function PortalSelector({ 
  variant = 'dropdown', 
  showDescription = true,
  className = '' 
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Get user's accessible portals
  const accessiblePortals = user ? getAccessiblePortals(user.role) : [];
  const currentPortal = getCurrentPortal(location.pathname);
  const hasMultiplePortals = accessiblePortals.length > 1;

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handlePortalChange = (portal) => {
    setIsOpen(false);
    navigate(portal.path);
  };

  // Don't render if user has only one portal
  if (!hasMultiplePortals) {
    return null;
  }

  // Compact variant - just icon with badge
  if (variant === 'compact') {
    return (
      <div ref={dropdownRef} className={`relative ${className}`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`
            flex items-center gap-2 px-3 py-2 rounded-lg
            bg-white/[0.03] border border-white/10 
            hover:bg-white/[0.06] hover:border-white/20
            transition-all duration-200
          `}
          data-testid="portal-selector-compact"
        >
          {currentPortal && (
            <currentPortal.icon className={`w-4 h-4 ${currentPortal.color.split(' ')[0]}`} />
          )}
          <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <PortalDropdown
            portals={accessiblePortals}
            currentPortal={currentPortal}
            onSelect={handlePortalChange}
            showDescription={showDescription}
          />
        )}
      </div>
    );
  }

  // Full variant - shows in sidebar
  if (variant === 'full') {
    return (
      <div className={`space-y-1.5 ${className}`}>
        <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-600 font-medium px-1">
          Cambiar Portal
        </span>
        {accessiblePortals.map(portal => {
          const isActive = currentPortal?.id === portal.id;
          const Icon = portal.icon;
          return (
            <button
              key={portal.id}
              onClick={() => handlePortalChange(portal)}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                border transition-all duration-200
                ${isActive 
                  ? portal.activeColor + ' text-white' 
                  : 'bg-white/[0.02] border-white/[0.06] text-zinc-400 hover:bg-white/[0.04] hover:border-white/10'
                }
              `}
              data-testid={`portal-btn-${portal.id}`}
            >
              <Icon className={`w-4 h-4 ${isActive ? 'text-current' : portal.color.split(' ')[0]}`} />
              <span className="text-sm font-medium flex-1 text-left">{portal.label}</span>
              {isActive && <Check className="w-4 h-4" />}
            </button>
          );
        })}
      </div>
    );
  }

  // Default dropdown variant
  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-3 px-4 py-2.5 rounded-xl
          bg-white/[0.03] border border-white/10 
          hover:bg-white/[0.06] hover:border-white/20
          transition-all duration-200 min-w-[200px]
        `}
        data-testid="portal-selector"
      >
        {currentPortal ? (
          <>
            <div className={`p-1.5 rounded-lg ${currentPortal.color}`}>
              <currentPortal.icon className="w-4 h-4" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-white">{currentPortal.label}</p>
              {showDescription && (
                <p className="text-[10px] text-zinc-500">{currentPortal.description}</p>
              )}
            </div>
          </>
        ) : (
          <span className="text-sm text-zinc-400">Seleccionar portal</span>
        )}
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <PortalDropdown
          portals={accessiblePortals}
          currentPortal={currentPortal}
          onSelect={handlePortalChange}
          showDescription={showDescription}
        />
      )}
    </div>
  );
}

/**
 * Portal Dropdown Menu
 */
function PortalDropdown({ portals, currentPortal, onSelect, showDescription }) {
  return (
    <div className="absolute top-full left-0 right-0 mt-2 py-2 rounded-xl bg-[#0a0a0a] border border-white/10 shadow-2xl z-50 min-w-[220px]">
      <p className="px-4 py-1.5 text-[10px] text-zinc-600 uppercase tracking-wider">
        Cambiar Portal
      </p>
      {portals.map(portal => {
        const isActive = currentPortal?.id === portal.id;
        const Icon = portal.icon;
        return (
          <button
            key={portal.id}
            onClick={() => onSelect(portal)}
            className={`
              w-full flex items-center gap-3 px-4 py-3
              transition-all duration-200
              ${isActive 
                ? 'bg-white/10 text-white' 
                : 'text-zinc-400 hover:bg-white/5 hover:text-white'
              }
            `}
            data-testid={`portal-option-${portal.id}`}
          >
            <div className={`p-1.5 rounded-lg ${portal.color}`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium">{portal.label}</p>
              {showDescription && (
                <p className="text-[10px] text-zinc-500">{portal.description}</p>
              )}
            </div>
            {isActive && <Check className="w-4 h-4 text-emerald-400" />}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Portal Indicator - Shows current portal as a badge
 */
export function PortalIndicator({ className = '' }) {
  const location = useLocation();
  const currentPortal = getCurrentPortal(location.pathname);

  if (!currentPortal) return null;

  const Icon = currentPortal.icon;

  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${currentPortal.color} ${className}`}>
      <Icon className="w-3.5 h-3.5" />
      <span className="text-xs font-medium">{currentPortal.label}</span>
    </div>
  );
}

export default PortalSelector;
